import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';
import { extendTailwindMerge } from 'tailwind-merge';

import { Tooltip } from '../tooltip/Tooltip';
import { PersonProgressIcon } from './icons/PersonProgressIcon';
import { PublishProgressIcon } from './icons/PublishProgressIcon';
import { ReviewProgressIcon } from './icons/ReviewProgressIcon';

export { calculateStatsProgress, statsProgressColors } from './stats-tokens';

const PROGRESS_COLOR_THRESHOLD = 0.1;

export type StatsRowVariant = 'default' | 'item' | 'trigger';

const getProgressColor = (progress: number, activeColor: string, inactiveColor: string): string => {
    return progress > PROGRESS_COLOR_THRESHOLD ? activeColor : inactiveColor;
};

const variantClasses: Record<StatsRowVariant, string> = {
    default: 'text-text-content-strong in-data-[is-active=true]:!text-text-on-accent',
    item: 'text-text-content-strong in-data-[is-active=true]:!text-text-on-accent text-body-xxs font-normal leading-[1.25rem]',
    trigger: 'text-text-content-strong in-data-[is-active=true]:!text-text-on-accent text-body-xs font-medium',
};

// Accept the named spacing tokens from ui/source/theme/primitives.css (gap-s, gap-m, ...) so caller overrides win.
const mergeStatsRowClasses = extendTailwindMerge({
    extend: { theme: { spacing: [() => true] } },
});

export const StatsRow: FunctionComponent<
    HTMLAttributes<HTMLDivElement> & {
        readonly variant?: StatsRowVariant;
    }
> = ({ children, className, variant = 'default', ...props }) => (
    <div
        className={mergeStatsRowClasses(
            'flex items-center flex-wrap text-left font-variant-numeric tabular-nums gap-s',
            variantClasses[variant],
            className,
        )}
        {...props}
    >
        {children}
    </div>
);

export const StatsLabel: FunctionComponent<
    HTMLAttributes<HTMLSpanElement> & {
        readonly 'data-testid'?: string;
    }
> = ({ children, className, ...props }) => (
    <span
        className={clsx(
            'inline-flex shrink-0 items-center gap-xxs whitespace-nowrap text-body-xxs font-medium in-data-[is-active=true]:!text-text-on-accent',
            className,
        )}
        {...props}
    >
        {children}
    </span>
);

export const StatsSeparator: FunctionComponent = () => (
    // biome-ignore lint/a11y/noSvgWithoutTitle: suppressed due to migration
    <svg className="inline-block stroke-current" height="1rem" viewBox="0 0 2 10" width="1">
        <rect fill="currentColor" height="10" rx="0.5" width="1" x="0.666626" />
    </svg>
);

export const StatsCount: FunctionComponent<{
    readonly children: ReactNode;
    readonly count: number;
    readonly 'data-testid'?: string;
    readonly tooltip: string;
}> = ({ children, count, 'data-testid': testId, tooltip }) => (
    <Tooltip>
        <Tooltip.Trigger asChild={true}>
            <StatsLabel data-testid={testId}>
                {children}
                {count}
            </StatsLabel>
        </Tooltip.Trigger>
        <Tooltip.Portal>
            <Tooltip.Content side="top">{tooltip}</Tooltip.Content>
        </Tooltip.Portal>
    </Tooltip>
);

export const StatsProgress: FunctionComponent<{
    readonly colorBase: string;
    readonly colorFill: string;
    readonly count: number;
    readonly 'data-testid'?: string;
    readonly icon: 'count' | 'publish' | 'review';
    readonly progress: number;
    readonly showCount?: boolean;
    readonly tooltip: string;
}> = ({ colorBase, colorFill, count, 'data-testid': testId, icon, progress, showCount = true, tooltip }) => {
    const ProgressIcon =
        icon === 'review' ? ReviewProgressIcon : icon === 'publish' ? PublishProgressIcon : PersonProgressIcon;
    const countColor = getProgressColor(progress, colorFill, colorBase);

    if (progress === 0) {
        return null;
    }

    return (
        <Tooltip>
            <Tooltip.Trigger asChild={true}>
                <span
                    className="inline-flex items-center gap-xxs text-body-xxs font-semibold tabular-nums"
                    data-testid={testId}
                >
                    <span className="inline-flex items-center justify-center">
                        <ProgressIcon colorBase={colorBase} colorFill={colorFill} progress={progress} />
                    </span>
                    {showCount && (
                        <span
                            className="font-semibold in-data-[is-active=true]:!text-text-on-accent"
                            style={{ color: countColor }}
                        >
                            {count}
                        </span>
                    )}
                </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top">{tooltip}</Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};
